/* eslint-disable react-hooks/exhaustive-deps */
import { produce } from 'immer';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import useBreakpoints from '../../../../../../common/hooks/useBreakpoints';
import useMount from '../../../../../../common/hooks/useMount';
import Breadcrumbs from '../../../../../../common/widgets/Breadcrumbs';
import Loader from '../../../../../../common/widgets/Loader';
import { TaxPayer } from '../../../../constants';
import { BuyerTypes, FilterTypes } from '../../../../enums';
import { PriceRange } from '../../../../models/PriceRange';
import { ProductListModel } from '../../../../models/Product';
import { resources } from '../../../../resources/resources';
import { filterService } from '../../../../services/filters';
import { productsService } from '../../../../services/products';
import { searchProductService } from '../../../../services/products/search';
import applicationCrumbs from '../../../ApplicationCrumbs';
import ProductListPanel from '../../../CategoryPage/Components/ProductsListPanel';
import { queryParamKeys } from '../../../CategoryPage/constants';
import {
  AvailableInFilterState,
  BrandFilterState,
  BuyerTypeFilterState,
  FilterSelectionsState,
  PriceRangeFilterState,
  ProductListState,
  ProductsOnOfferFilterState,
  TaxationLevelFilterState,
  filterSelectionDefaults,
} from '../../../CategoryPage/types';
import {
  getAvailableInList,
  getBrandList,
  getDiscountedProductsCount,
  getFiltredProducts,
  getPriceRange,
  getSortedProduts,
  getTaxList,
  getUpdatedFilters,
  hasSelectedFilters,
} from '../../../CategoryPage/utils';
import FilterPanel from '../../../Common/Filters';
import { getProductIdentifier, getQueryParams } from '../../utils';
import MobileSearchContent from '../MobileSearch';
import CategoryFilter from './components/CategoryFilter';
import useSearchProductsCacheKey from './useSearchProductsCacheKey';

export function useCachedEntities<T>(key: string) {
  const location = useLocation();
  const [data, setData] = useState<T | null>(() => {
    const stateData = location.state?.products;
    if (stateData) return stateData;

    const saved = sessionStorage.getItem(key);
    return saved ? JSON.parse(saved) : null;
  });

  useEffect(() => {
    const stateData = location.state?.products;
    if (stateData) {
      setData(stateData);
      sessionStorage.setItem(key, JSON.stringify(stateData));
    }
  }, [location.state, key]);

  return [data] as const;
}

export default function SearchResults() {
  const cacheKey = useSearchProductsCacheKey();
  const [products] = useCachedEntities<ProductListModel[]>(cacheKey);

  const { t: translate } = useTranslation();
  const { isTablet } = useBreakpoints();

  const buyerTypeList = useMemo(
    () => [
      {
        buyerType: BuyerTypes.Employee,
        name: translate(resources.filters.buyerType.employee),
      },
      {
        buyerType: BuyerTypes.Dependent,
        name: translate(resources.filters.buyerType.dependent),
      },
    ],
    [translate],
  );

  const navigate = useNavigate();
  const ref = useRef(null);
  const filtersSectionRef = useRef<HTMLTableSectionElement | null>(null);

  const entityIdentifier = getProductIdentifier(getQueryParams());
  const crumbs = applicationCrumbs(translate, entityIdentifier?.name);
  const searchResult = products!;

  // #region state
  const [productsSearched, setProductsSearched] = useState<ProductListState>({
    productList: undefined,
    filtredProductList: undefined,
  });
  const [productsOnOfferFilterState, setProductsOnOfferFilterState] =
    useState<ProductsOnOfferFilterState>({
      discountedProductsCount: 0,
    });
  const [priceRangeFilterState, setPriceRangeFilterState] =
    useState<PriceRangeFilterState>({
      priceRange: undefined,
    });
  const [filterSelectionState, setFilterSelectionState] =
    useState<FilterSelectionsState>(filterSelectionDefaults);
  const [currentFilterType, setCurrentFilterType] = useState<number>();
  const [selectedCategories, setSelectedCategories] = useState<Set<number>>(
    new Set<number>(),
  );

  const [availableInFilterState, setAvailableInFilterState] =
    useState<AvailableInFilterState>({
      availableInList: undefined,
      filteredAvailableInList: undefined,
    });
  const [taxationLevelFilterState, setTaxationLevelFilterState] =
    useState<TaxationLevelFilterState>({
      taxationLevelList: undefined,
      filteredTaxationLevelList: undefined,
    });

  const [
    employerTaxationLevelFilterState,
    setEmployerTaxationLevelFilterState,
  ] = useState<TaxationLevelFilterState>({
    taxationLevelList: undefined,
    filteredTaxationLevelList: undefined,
  });

  const [brandFilterState, setBrandFilterState] = useState<BrandFilterState>({
    brandList: undefined,
    filteredBrandList: undefined,
  });
  const [buyerTypeFilterState, setBuyerTypeFilterState] =
    useState<BuyerTypeFilterState>({
      buyerTypeList,
      filteredBuyerTypeList: buyerTypeList,
    });
  //endregion

  const categoriesIds = useMemo(
    () =>
      _.uniq(
        searchResult?.length
          ? searchResult?.map(res => res.categoryId)
          : productsSearched.productList?.map(res => res.categoryId),
      ),
    [searchResult, productsSearched.productList],
  );

  const hasFilters =
    productsSearched.productList?.length !==
    productsSearched.filtredProductList?.length;

  const [searchParams] = useSearchParams();
  const skipParam = searchParams.get(queryParamKeys.skip);
  const initialSkip = skipParam ? Number(skipParam) : undefined;

  const takeParam = searchParams.get(queryParamKeys.take);
  const initialTake = takeParam ? Number(takeParam) : undefined;

  // #region data loader
  const loadProducts = async (abortController?: AbortController) => {
    if (entityIdentifier?.name) {
      if (_.isEmpty(searchResult)) {
        (async () => {
          const result = entityIdentifier?.brandId
            ? await productsService.getProductsForBrand(
                entityIdentifier.brandId,
                abortController,
              )
            : await searchProductService.getSearchProducts(
                entityIdentifier.name,
                abortController,
              );

          if (!result.hasErrors) {
            result.data.length > 0 || !entityIdentifier.name
              ? setProductsSearched({
                  productList: result.data,
                  filtredProductList: result.data,
                })
              : navigate(`/products/no-result/${entityIdentifier.name}`, {
                  state: {
                    suggestions: [],
                  },
                });
          }
        })();
      } else {
        setProductsSearched(
          produce(d => {
            d.productList = searchResult;
            d.filtredProductList = searchResult;
          }),
        );

        setProductsOnOfferFilterState({
          discountedProductsCount: getDiscountedProductsCount(searchResult),
        });
        setPriceRangeFilterState({ priceRange: getPriceRange(searchResult) });
      }
    }
  };

  const loadBrandList = async () => {
    setBrandFilterState({
      brandList: undefined,
      filteredBrandList: undefined,
    });

    const result =
      await filterService.getBrandFilterForCategories(categoriesIds);
    if (!result.hasErrors) {
      setBrandFilterState({
        brandList: result.data,
        filteredBrandList: getBrandList(
          productsSearched.productList ?? [],
          result.data,
        ),
      });
    }
  };

  const loadTaxationLevelList = async () => {
    setTaxationLevelFilterState({
      taxationLevelList: undefined,
      filteredTaxationLevelList: undefined,
    });
    const result = await filterService.getTaxFilterForCategories(categoriesIds);
    if (!result.hasErrors) {
      const employeeTaxationLevels = result.data.filter(
        t => t.payer === TaxPayer.Employee,
      );
      const employerTaxationLevels = result.data.filter(
        t => t.payer === TaxPayer.Employer,
      );
      setTaxationLevelFilterState({
        taxationLevelList: employeeTaxationLevels,
        filteredTaxationLevelList: getTaxList(
          productsSearched.productList ?? [],
          employeeTaxationLevels,
        ),
      });
      setEmployerTaxationLevelFilterState({
        taxationLevelList: employerTaxationLevels,
        filteredTaxationLevelList: getTaxList(
          productsSearched.productList ?? [],
          employerTaxationLevels,
        ),
      });
    }
  };

  const loadAvailableInList = async () => {
    setAvailableInFilterState({
      availableInList: undefined,
      filteredAvailableInList: undefined,
    });
    const result =
      await filterService.getCountyFilterForCategories(categoriesIds);
    if (!result.hasErrors) {
      setAvailableInFilterState({
        availableInList: result.data,
        filteredAvailableInList: getAvailableInList(
          productsSearched.productList ?? [],
          result.data,
        ),
      });
    }
  };
  // #endregion

  // #region filter change handlers
  const handlePriceRangeFilterChanged = useCallback(
    (range: PriceRange) => {
      setFilterSelectionState(
        produce(draft => {
          draft.priceRange = range;
        }),
      );
      setCurrentFilterType(FilterTypes.PriceRange);
    },
    [filterSelectionState.priceRange],
  );

  const handleAvailableInFilterChanged = useCallback((countyIds: number[]) => {
    setFilterSelectionState(
      produce(draft => {
        draft.selectedCountyIds = countyIds;
      }),
    );
    setCurrentFilterType(FilterTypes.AvailableIn);
  }, []);

  const handleBrandFilterChanged = useCallback((brandIds: number[]) => {
    setFilterSelectionState(
      produce(draft => {
        draft.selectedBrandIds = brandIds;
      }),
    );
    setCurrentFilterType(FilterTypes.Brand);
  }, []);

  const handleBuyerTypeFilterChanged = useCallback(
    (buyerTypes: BuyerTypes[]) => {
      setFilterSelectionState(
        produce(draft => {
          draft.selectedBuyerTypes = buyerTypes;
        }),
      );
      setCurrentFilterType(FilterTypes.BuyerType);
    },
    [],
  );

  const handleTaxFilterChanged = useCallback((taxClassIds: number[]) => {
    setFilterSelectionState(
      produce(draft => {
        draft.selectedTaxClassIds = taxClassIds;
      }),
    );
    setCurrentFilterType(FilterTypes.TaxationLevel);
  }, []);

  const handleEmployerTaxFilterChanged = useCallback(
    (taxClassIds: number[]) => {
      setFilterSelectionState(
        produce(draft => {
          draft.selectedEmployerTaxClassIds = taxClassIds;
        }),
      );
      setCurrentFilterType(FilterTypes.EmployerTaxationLevel);
    },
    [],
  );

  const handleProductsOnOfferFilterChanged = useCallback(
    (isSelected: boolean) => {
      setFilterSelectionState(
        produce(draft => {
          draft.showDiscountedProducts = isSelected;
        }),
      );
      setCurrentFilterType(FilterTypes.ShowDiscountedProducts);
    },
    [],
  );

  const handleSortingOrderChanged = (id: number) => {
    setProductsSearched(
      produce(draft => {
        draft.filtredProductList = getSortedProduts[id](
          draft.filtredProductList,
        );
      }),
    );
  };

  const handleCategoryChanged = useCallback((categoriesIds: Set<number>) => {
    setFilterSelectionState(
      produce(draft => {
        draft.selectedCategories = categoriesIds;
      }),
    );
    setCurrentFilterType(FilterTypes.Category);
  }, []);

  const handleClearFilters = () => {
    setFilterSelectionState(filterSelectionDefaults);
    setSelectedCategories(new Set<number>());

    if (productsSearched.productList) {
      setPriceRangeFilterState({
        priceRange: getPriceRange(productsSearched.productList),
      });
    }

    setProductsSearched(
      produce(d => {
        d.filtredProductList = d.productList;
      }),
    );
  };
  //#endregion

  useEffect(() => {
    if (!isTablet && !entityIdentifier) navigate('/');
  }, []);

  useMount(
    abortController => {
      loadProducts(abortController);
      document.title = translate(resources.searchPage.title.results);
    },
    [entityIdentifier?.name, searchResult],
  );

  useEffect(() => {
    if (categoriesIds?.length > 0) {
      loadBrandList();
      loadTaxationLevelList();
      loadAvailableInList();
    }
  }, [productsSearched.productList, categoriesIds]);

  useEffect(() => {
    setProductsSearched(
      produce(d => {
        d.filtredProductList = getFiltredProducts(
          productsSearched.productList,
          filterSelectionState,
        );
      }),
    );
  }, [
    filterSelectionState.selectedBrandIds,
    filterSelectionState.selectedBuyerTypes,
    filterSelectionState.selectedCountyIds,
    filterSelectionState.selectedTaxClassIds,
    filterSelectionState.showDiscountedProducts,
    filterSelectionState.selectedCategories,
    filterSelectionState.priceRange,
    productsSearched.productList,
  ]);

  useEffect(() => {
    const filters = getUpdatedFilters(
      productsSearched.productList,
      productsSearched.filtredProductList,
      availableInFilterState.availableInList,
      brandFilterState.brandList,
      taxationLevelFilterState.taxationLevelList,
      employerTaxationLevelFilterState.taxationLevelList,
      buyerTypeFilterState.buyerTypeList,
      selectedCategories,
      currentFilterType === FilterTypes.PriceRange,
      hasSelectedFilters(filterSelectionState),
    );
    if (filters) {
      if (currentFilterType !== FilterTypes.AvailableIn)
        setAvailableInFilterState(
          produce(d => {
            d.filteredAvailableInList = filters.filteredCounties;
          }),
        );
      if (currentFilterType !== FilterTypes.TaxationLevel)
        setTaxationLevelFilterState(
          produce(d => {
            d.filteredTaxationLevelList = filters.filteredTax;
          }),
        );
      if (currentFilterType !== FilterTypes.Brand)
        setBrandFilterState(
          produce(d => {
            d.filteredBrandList = filters.filteredBrands;
          }),
        );
      if (currentFilterType !== FilterTypes.BuyerType)
        setBuyerTypeFilterState(
          produce(d => {
            d.filteredBuyerTypeList = filters.filteredBuyerTypes;
          }),
        );
      if (currentFilterType !== FilterTypes.Category) {
        setSelectedCategories(filters.filteredSelectedCategories);
      }
      if (currentFilterType !== FilterTypes.PriceRange)
        setPriceRangeFilterState({ priceRange: filters.priceRange });
      if (currentFilterType !== FilterTypes.ShowDiscountedProducts)
        setProductsOnOfferFilterState({
          discountedProductsCount: filters.discountedProductsCount,
        });
    }
  }, [productsSearched.filtredProductList]);

  useEffect(() => {
    if (!productsSearched.filtredProductList && productsSearched.productList) {
      setProductsSearched(
        produce(d => {
          d.filtredProductList = d.productList;
        }),
      );
    }
  }, [productsSearched.filtredProductList]);

  useEffect(() => {
    setFilterSelectionState(filterSelectionDefaults);
  }, [productsSearched.productList]);

  return (
    <>
      {!isTablet ? (
        <>
          <Breadcrumbs entries={[crumbs.home, crumbs.search]} />
          <section
            className="section-main section-actions search-results-page"
            ref={filtersSectionRef}
          >
            <div className="container" ref={ref}>
              <div className="area-actions">
                <CategoryFilter
                  filteredProducts={productsSearched.filtredProductList}
                  selectedCategoryIds={selectedCategories}
                  handleChange={handleCategoryChanged}
                />
                <FilterPanel
                  availableInList={
                    availableInFilterState.filteredAvailableInList
                  }
                  brandList={brandFilterState.filteredBrandList}
                  buyerTypeList={buyerTypeFilterState.filteredBuyerTypeList}
                  taxationLevelList={
                    taxationLevelFilterState.filteredTaxationLevelList
                  }
                  employerTaxationLevelList={
                    employerTaxationLevelFilterState.filteredTaxationLevelList
                  }
                  priceRange={priceRangeFilterState.priceRange}
                  discountedProdutsCount={
                    productsOnOfferFilterState.discountedProductsCount
                  }
                  prevSelectionState={filterSelectionState}
                  hasActiveFilters={hasFilters}
                  handlePriceRangeFilterChanged={handlePriceRangeFilterChanged}
                  handleAvailableInFilterChanged={
                    handleAvailableInFilterChanged
                  }
                  handleBrandFilterChanged={handleBrandFilterChanged}
                  handleBuyerTypeFilterChanged={handleBuyerTypeFilterChanged}
                  handleTaxFilterChanged={handleTaxFilterChanged}
                  handleEmployerTaxFilterChanged={
                    handleEmployerTaxFilterChanged
                  }
                  handleProductsOnOfferFilterChanged={
                    handleProductsOnOfferFilterChanged
                  }
                  handleClearFilters={handleClearFilters}
                  filteredProducts={productsSearched.filtredProductList}
                />
              </div>

              <div className="area-content">
                <div className="search-results-title">
                  <h1>
                    {`${translate(
                      resources.searchPage.title.results,
                    )}: ${entityIdentifier?.name}`}
                  </h1>
                  <Loader
                    loading={!productsSearched.filtredProductList}
                    loader={<Skeleton />}
                  >
                    <span>{`${productsSearched.filtredProductList
                      ?.length} ${translate(
                      resources.searchPage.products,
                    )}`}</span>
                  </Loader>
                </div>
                <ProductListPanel
                  productList={productsSearched.filtredProductList}
                  isDiscountVoucherList={false}
                  isBenefitBucksList={false}
                  handleSortingOrderChanged={handleSortingOrderChanged}
                  isSearchResults={true}
                  initialSkip={initialSkip}
                  initialTake={initialTake}
                />
              </div>
            </div>
          </section>
        </>
      ) : (
        <MobileSearchContent
          searchProducts={productsSearched}
          setSearchProducts={setProductsSearched}
          filters={{
            filteredAvailableInList:
              availableInFilterState.filteredAvailableInList,
            filteredBrandList: brandFilterState.filteredBrandList,
            filteredBuyerTypeList: buyerTypeFilterState.filteredBuyerTypeList,
            filteredTaxationLevelList:
              taxationLevelFilterState.filteredTaxationLevelList,
            filteredEmployerTaxationLevelList:
              employerTaxationLevelFilterState.filteredTaxationLevelList,
            priceRange: priceRangeFilterState.priceRange,
            discountedProductsCount:
              productsOnOfferFilterState.discountedProductsCount,
            selectionFilterState: filterSelectionState,
            hasActiveFilters: hasFilters,
            priceRangeFilterChanged: handlePriceRangeFilterChanged,
            availableInFilterChanged: handleAvailableInFilterChanged,
            brandFilterChanged: handleBrandFilterChanged,
            buyerTypeFilterChanged: handleBuyerTypeFilterChanged,
            taxFilterChanged: handleTaxFilterChanged,
            employerTaxFilterChanged: handleEmployerTaxFilterChanged,
            productsOnOfferFilterChanged: handleProductsOnOfferFilterChanged,
            clearFilters: handleClearFilters,
          }}
        />
      )}
    </>
  );
}
